import { useEffect, useMemo } from 'react';
import { bulkColors } from 'src/constants/styles';
import ReBarChart from 'switch-fe-shared/components/Charts/ReBarChart';
import { BarSeries } from 'switch-fe-shared/components/Charts/recharts/types';
import { formatNumber, graphFileName } from 'switch-fe-shared/utils/formatting';
import { AggregateP1SplitGroupBy } from 'switch-shared/modules/crud/utility-data/utility-data.dto';
import {
  UtilityTypeOrAll,
  convertToAndFromBase,
} from 'switch-shared/modules/crud/utility/utility.enums';
import { DurationUnit } from 'switch-shared/utils/duration.enum';

import { useGetAggregateP1SplitQuery } from '../../../services/switch-rest-api/utility-data.service';
import { getUtilityUnits } from '../../../utils/formatters';

import '../../../styles/chart/LoadingChart.css';
import { isUtilityType, toConcreteUtilityType } from '../../../utils/selection';
import { Metrics } from '../../dashboard/components/widgets/UtilityUsageWidget';

interface Props {
  handleMetrics: (metrics: Metrics, priorMetrics?: Metrics) => void;
  appId: string;
  utilityType: UtilityTypeOrAll;
  startDate: Date;
  endDate: Date;
  bucketSize: number;
  bucketUnit: DurationUnit;
  zoneId?: string;
  includePriorPeriod?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
}

const AppUsageGraph = ({
  handleMetrics,
  appId,
  utilityType,
  startDate,
  endDate,
  bucketSize,
  bucketUnit,
  zoneId,
  includePriorPeriod = true,
  onLoadingChange,
}: Props) => {
  /* Hooks & Use Effects */
  const id = graphFileName(
    `app-usage`,
    startDate.toISOString(),
    endDate.toISOString()
  );
  /* States */

  /* API Calls */

  const {
    data: utilityData,
    isLoading,
    isFetching,
  } = useGetAggregateP1SplitQuery(
    {
      startTimeISO: startDate.toISOString(),
      endTimeISO: endDate.toISOString(),
      applicationId: appId,
      ...(isUtilityType(utilityType) && { utilityType }),
      bucketSize,
      bucketUnit,
      groupBy: 'bucket' as AggregateP1SplitGroupBy,
      zoneId,
      includePriorPeriod,
    },
    { pollingInterval: 300_000 }
  );

  const isGraphLoading = isLoading;

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isGraphLoading);
      return () => {
        onLoadingChange(false);
      };
    }
    return undefined;
  }, [isGraphLoading, onLoadingChange]);

  /* Functions & Functional Components */

  const series = useMemo<BarSeries[]>(
    () => [
      {
        name: 'Bulk Usage',
        data: (utilityData ? utilityData.buckets : []).map((ele) =>
          ele.deltaP1Bulk != null
            ? convertToAndFromBase(
                ele.deltaP1Bulk,
                toConcreteUtilityType(utilityType),
                true
              )
            : 0
        ),
        group: 'bulk',
      },
      {
        name: 'Sub-Meter Usage',
        data: (utilityData ? utilityData.buckets : []).map((ele) =>
          ele.deltaP1SubMeter != null
            ? convertToAndFromBase(
                ele.deltaP1SubMeter,
                toConcreteUtilityType(utilityType),
                true
              )
            : 0
        ),
        group: 'tenant',
      },
      {
        name: 'Unmetered Usage',
        data: (utilityData ? utilityData.buckets : []).map((ele) =>
          ele.deltaP1Unmetered != null
            ? convertToAndFromBase(
                ele.deltaP1Unmetered,
                toConcreteUtilityType(utilityType),
                true
              )
            : 0
        ),
        group: 'tenant',
      },
    ],
    [utilityData, utilityType]
  );

  const categories: string[] = useMemo(
    () =>
      utilityData?.buckets
        ? utilityData.buckets.map((bucket) => {
            if (bucket.timestampISO) {
              return bucket.timestampISO;
            }
            const name = (bucket as any).name as string;
            return name;
          })
        : [],
    [utilityData]
  );

  const unit = useMemo(() => {
    return getUtilityUnits(utilityType, true);
  }, [utilityType]);

  const tooltipTotals = useMemo(
    () => [
      { label: 'Total Bulk', prefixes: ['Bulk'] },
      { label: 'Total Sub-Meter', prefixes: ['Sub-Meter'] },
    ],
    []
  );

  useEffect(() => {
    const totalBulk = utilityData?.totalDeltaP1Bulk;
    const totalSub = utilityData?.totalDeltaP1SubMeter;
    const totalCommunal = utilityData?.totalDeltaP1Unmetered;

    const priorBulk = utilityData?.priorTotalDeltaP1Bulk;
    const priorSub = utilityData?.priorTotalDeltaP1SubMeter;
    const priorCommunal = utilityData?.priorTotalDeltaP1Unmetered;

    const currentMetrics = {
      bulk:
        totalBulk != null
          ? convertToAndFromBase(
              totalBulk,
              toConcreteUtilityType(utilityType),
              true
            )
          : 0,
      subMeter:
        totalSub != null
          ? convertToAndFromBase(
              totalSub,
              toConcreteUtilityType(utilityType),
              true
            )
          : 0,
      communal:
        totalCommunal != null
          ? convertToAndFromBase(
              totalCommunal,
              toConcreteUtilityType(utilityType),
              true
            )
          : 0,
    };

    const priorMetrics =
      priorBulk !== undefined ||
      priorSub !== undefined ||
      priorCommunal !== undefined
        ? {
            bulk:
              priorBulk != null
                ? convertToAndFromBase(priorBulk, utilityType, true)
                : 0,
            subMeter:
              priorSub != null
                ? convertToAndFromBase(priorSub, utilityType, true)
                : 0,
            communal:
              priorCommunal != null
                ? convertToAndFromBase(priorCommunal, utilityType, true)
                : 0,
          }
        : undefined;

    handleMetrics(currentMetrics, priorMetrics);
  }, [utilityData, utilityType, handleMetrics]);

  return (
    <div
      className={isFetching ? 'pulsing-chart' : undefined}
      style={{ height: '100%', paddingTop: 25 }}
    >
      <ReBarChart
        key={id}
        isLoading={isLoading}
        series={series}
        categories={categories}
        colors={[
          bulkColors.BULK_COLOR,
          bulkColors.TENANT_COLOR,
          bulkColors.COMMUNAL_COLOR,
        ]}
        yTitle={`Usage (${unit})`}
        unit={unit}
        stackGroups
        showLegend
        fileName={id}
        bucketOptions={{ unit: bucketUnit }}
        yFormatter={(val) => formatNumber(Math.abs(val))}
        tooltipTotals={tooltipTotals}
      />
    </div>
  );
};

export default AppUsageGraph;
